"use client";

import ConsoleLogo from "@/assets/console-logo.svg";
import ContainerIcon from "@/assets/container-icon.svg";
import { Button } from "@spheron/ui-library";
import GpuIcon from "@spheron/ui-library/dist/assets/compute/gpu.svg";
import ArrowRight from "@spheron/ui-library/dist/assets/arrow-right-1.svg";

// Enterprise GPU destination. UTM + referral params are attached so every visit
// from this phase-out page can be attributed to the Console wind-down in Google
// Analytics on app.spheron.ai. `content` distinguishes which element was clicked.
const buildEnterpriseUrl = (content: string) => {
  const params = new URLSearchParams({
    utm_source: "spheron_console",
    utm_medium: "referral",
    utm_campaign: "console_phase_out",
    utm_content: content,
    ref: "spheron_console",
  });
  return `https://app.spheron.ai/?${params.toString()}`;
};

const SUPPORT_EMAIL = "info@spheron.network";

const PhaseOutClient = () => {
  const openEnterprise = (content: string) => {
    window.open(buildEnterpriseUrl(content), "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="w-full border-b border-base-border dark:border-dark-base-border">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center">
          <ConsoleLogo className="h-8 text-black dark:text-white" />
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-20 md:py-28">
        <div className="max-w-[640px] w-full flex flex-col items-center text-center">
          {/* Notice pill */}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-base-border dark:border-dark-base-border bg-base-fg dark:bg-dark-base-fg">
            <span className="w-2 h-2 rounded-full bg-action-primary-default" />
            <span className="text-xs font-medium tracking-wide uppercase text-base-sub-text-color dark:text-dark-base-sub-text-color">
              Platform Update
            </span>
          </div>

          {/* Icon */}
          <div className="mt-8 flex items-center justify-center w-16 h-16 rounded-2xl border border-base-border dark:border-dark-base-border bg-base-fg dark:bg-dark-base-fg">
            <ContainerIcon className="w-9 h-9 text-logo-primary dark:text-dark-logo-primary" />
          </div>

          {/* Heading */}
          <h1 className="mt-8 text-3xl md:text-4xl font-semibold leading-tight text-base-heading-text-color dark:text-dark-base-heading-text-color">
            We&rsquo;re phasing out the Spheron Decentralized GPU Renting
            Platform
          </h1>

          {/* Body */}
          <p className="mt-5 text-base md:text-lg leading-relaxed text-base-para-text-color dark:text-dark-base-para-text-color">
            After careful thought, we&rsquo;ve decided to retire Spheron Console
            and will no longer be maintaining it. Deployments and account
            features are now disabled. Thank you to every builder who trusted us
            to run their workloads.
          </p>

          {/* Enterprise card */}
          <div className="mt-10 w-full rounded-2xl border border-base-border dark:border-dark-base-border bg-base-fg dark:bg-dark-base-fg p-6 md:p-8 text-left">
            <h2 className="text-lg md:text-xl font-semibold text-base-heading-text-color dark:text-dark-base-heading-text-color">
              Looking for enterprise-grade GPUs?
            </h2>
            <p className="mt-2 text-sm md:text-base leading-relaxed text-base-para-text-color dark:text-dark-base-para-text-color">
              Spheron&rsquo;s next chapter is live. Get on-demand,
              enterprise-grade GPU compute built to scale, now at{" "}
              <a
                href={buildEnterpriseUrl("home_body_link")}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-base-link dark:text-dark-base-link hover:underline"
              >
                app.spheron.ai
              </a>
              .
            </p>
            <div className="mt-6">
              <Button
                label="Explore Enterprise GPUs"
                buttonType="primary"
                size="small"
                leftIcon={<GpuIcon className="w-5 h-5" />}
                onClick={() => openEnterprise("home_cta")}
              />
            </div>
          </div>

          {/* Contact */}
          <p className="mt-8 text-sm text-base-sub-text-color dark:text-dark-base-sub-text-color">
            Have questions? Reach our team at{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="inline-flex items-center gap-1 font-medium text-base-link dark:text-dark-base-link hover:underline"
            >
              {SUPPORT_EMAIL}
              <ArrowRight className="inline-block w-4 h-4" />
            </a>
          </p>
        </div>
      </main>
    </div>
  );
};

export default PhaseOutClient;
